"use client";

import { useQuery } from "@tanstack/react-query";
import type { BetterstackMonitor, UptimeData } from "@/lib/betterstack";

export function useUptime(fallback = 99.992): number {
  const { data } = useQuery<{ availability: number; monitors: unknown[] }, Error, number>({
    queryKey: ["uptime"],
    queryFn: () => fetch("/api/uptime").then((r) => r.json()),
    select: (d) => (typeof d === "number" ? d : (d?.availability ?? fallback)),
    placeholderData: { availability: fallback, monitors: [] },
  });
  return data ?? fallback;
}

export type UptimeStatus = BetterstackMonitor["status"];

export interface UptimeSummary {
  availability: number;
  status: UptimeStatus;
}

/** Live uptime summary from BetterStack — availability plus the current service
    status. Shares the ["uptime"] query with `useUptime`, so it adds no extra
    request. Falls back to a healthy default before the first response resolves. */
export function useUptimeData(fallbackAvailability = 99.992): UptimeSummary {
  const { data } = useQuery<UptimeData, Error, UptimeSummary>({
    queryKey: ["uptime"],
    queryFn: () => fetch("/api/uptime").then((r) => r.json()),
    select: (d) => ({
      availability: typeof d?.availability === "number" ? d.availability : fallbackAvailability,
      status: d?.monitors?.[0]?.status ?? "operational",
    }),
    placeholderData: { availability: fallbackAvailability, monitors: [] },
  });
  return data ?? { availability: fallbackAvailability, status: "operational" };
}
